import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { customerQueryKeys } from '@/lib/services/customers/customers.query'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import {
  getOrganizationBillingSettings,
  resetOrganizationBillingSettings,
  updateOrganizationBillingSettings,
  type OrganizationBillingSettingsScope,
} from './organization-billing-settings.api'
import type {
  ResetOrganizationBillingSettingsInput,
  UpdateOrganizationBillingSettingsInput,
} from './organization-billing-settings.types'

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type OrganizationBillingSettingsQueryKey = [AdminQueryRoot & OrganizationBillingSettingsScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const organizationBillingSettingsQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'organization-billing-settings' }, { organizationId }),
  ],
  detail: ({
    organizationId,
  }: OrganizationBillingSettingsScope): OrganizationBillingSettingsQueryKey =>
    buildScopedAdminQueryKey(organizationBillingSettingsQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'detail',
    }),
}

export const organizationBillingSettingsQueries = {
  detail: ({ organizationId }: OrganizationBillingSettingsScope) =>
    queryOptions({
      queryKey: organizationBillingSettingsQueryKeys.detail({ organizationId }),
      queryFn: getOrganizationBillingSettings,
    }),
}

export function useOrganizationBillingSettings({
  organizationId,
  ...params
}: OrganizationScope &
  QueryParams<ReturnType<typeof organizationBillingSettingsQueries.detail>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...organizationBillingSettingsQueries.detail({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

function invalidateBillingSettings(organizationId: number) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: organizationBillingSettingsQueryKeys.detail({ organizationId }),
    }),
    queryClient.invalidateQueries({
      queryKey: customerQueryKeys.billingDefaults({ organizationId }),
    }),
  ])
}

export function useUpdateOrganizationBillingSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateOrganizationBillingSettingsInput) =>
      updateOrganizationBillingSettings({ organizationId, ...values }),
    onSuccess: () => invalidateBillingSettings(organizationId),
  })
}

export function useResetOrganizationBillingSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ expectedVersion }: ResetOrganizationBillingSettingsInput) =>
      resetOrganizationBillingSettings({ organizationId, expectedVersion }),
    onSuccess: () => invalidateBillingSettings(organizationId),
  })
}
